import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { loadServerMonitoringFlags } from '../../context-api/api-services/ServerMonitoring.service';
import { getServerMonitoringFlagsCache } from '../../utils/serverMonitoringFlagsCache';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import { ServerMonitoringLogsModal } from './ServerMonitoringLogsModal';
import { ServerMonitoringStatusCell } from './ServerMonitoringStatusCell';
import {
  BACKEND_APPS,
  BACKEND_SIDEBAR_SECTION,
  ENV_COLUMNS,
  LAYOUT_MAX_WIDTH,
} from './serverMonitoringConstants';
import {
  buildServerFlagsLookup,
  formatLastUpdated,
  getLatestFlagsTimestamp,
  getServerFlagForCell,
} from './serverMonitoringUtils';
import './ServerMonitoringPage.css';

function EnvHeaderCell({ column }) {
  if (!column.hasInfo) {
    return column.label;
  }

  return (
    <span className="server-monitoring-table__header-label">
      {column.label}
      <Tooltip title={column.tooltip} arrow placement="top">
        <button
          type="button"
          className="server-monitoring-table__info-btn"
          aria-label={`${column.label} environment info`}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </button>
      </Tooltip>
    </span>
  );
}

const ServerMonitoring = () => {
  const { setSelectedMenuTab } = useContext(GlobalStateDataContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flags, setFlags] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [logsRecord, setLogsRecord] = useState(null);

  const flagsLookup = useMemo(() => buildServerFlagsLookup(flags), [flags]);

  const loadFlags = useCallback(async (forceRefresh = false) => {
    const hasCached = !forceRefresh && Boolean(getServerMonitoringFlagsCache()?.flags?.length);
    if (!hasCached) {
      setLoading(true);
    }
    setError(null);
    const result = await loadServerMonitoringFlags({ forceRefresh });
    if (!result.ok) {
      setFlags([]);
      setLastUpdated(null);
      setError(result.error || 'Could not load server monitoring flags.');
      setLoading(false);
      return;
    }
    setFlags(result.flags);
    setLastUpdated(getLatestFlagsTimestamp(result.flags) ?? result.refreshedAt);
    setLoading(false);
    setError(null);
  }, []);

  useEffect(() => {
    setSelectedMenuTab('server-monitoring');
  }, [setSelectedMenuTab]);

  useEffect(() => {
    void loadFlags();
  }, [loadFlags]);

  const handleOpenLogs = useCallback((record) => {
    setLogsRecord(record);
  }, []);

  const handleCloseLogs = useCallback(() => {
    setLogsRecord(null);
  }, []);

  return (
    <div className="server-monitoring-page">
      <div className="server-monitoring-topbar" style={{ maxWidth: LAYOUT_MAX_WIDTH }}>
        <h1 className="server-monitoring-topbar__title">Server Monitoring</h1>
        <div className="server-monitoring-topbar__subtitle">
          Backend service health across environments
        </div>
      </div>

      <div className="server-monitoring-shell" style={{ maxWidth: LAYOUT_MAX_WIDTH }}>
        <aside className="server-monitoring-toc">
          <div className="server-monitoring-toc-header">Sections</div>
          <div className="server-monitoring-toc-link" aria-current="page">
            <DnsOutlinedIcon sx={{ fontSize: 15, flexShrink: 0 }} />
            {BACKEND_SIDEBAR_SECTION.title}
          </div>
        </aside>

        <main className="server-monitoring-content">
          <p className="server-monitoring-last-updated">
            LastUpdated{' '}
            <span className="server-monitoring-last-updated__value">
              {formatLastUpdated(lastUpdated)}
            </span>
          </p>

          {error ? (
            <Alert severity="error" className="server-monitoring-error" onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          {loading ? (
            <div className="server-monitoring-loading">
              <CircularProgress size={28} sx={{ color: '#FF7A28' }} />
            </div>
          ) : (
            <div className="server-monitoring-table-wrap">
              <table className="server-monitoring-table">
                <thead>
                  <tr>
                    <th scope="col">App</th>
                    {ENV_COLUMNS.map((column) => (
                      <th key={column.key} scope="col">
                        <EnvHeaderCell column={column} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {BACKEND_APPS.map((app) => (
                    <tr key={app.id}>
                      <td className="server-monitoring-table__app">{app.name}</td>
                      {ENV_COLUMNS.map((column) => {
                        const record = getServerFlagForCell(flagsLookup, app.id, column.key);
                        return (
                          <td
                            key={`${app.id}-${column.key}`}
                            className="server-monitoring-table__status"
                          >
                            <ServerMonitoringStatusCell
                              record={record}
                              onOpenLogs={handleOpenLogs}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      <ServerMonitoringLogsModal
        open={Boolean(logsRecord)}
        record={logsRecord}
        onClose={handleCloseLogs}
      />
    </div>
  );
};

export default ServerMonitoring;
